import React, { useEffect, useState } from 'react'
import InputCustom from 'components/Inputs'
import CustomSelect from 'components/Select'
import useRedux from 'hooks/useRedux'
import * as campanhaActions from 'store/modules/backoffice/campanha/actions'
import { FiBriefcase } from 'react-icons/fi'
import { useSelector } from 'react-redux'
import { Col, Collapse, Input, Row } from 'reactstrap'
import { ApplicationState } from 'store'
import { CAMPANHA } from 'store/modules/backoffice/campanha/types'
import { SimpleTitleCard } from 'styles/global'
import { maskNumber, validarData } from 'util/masks'
import { toast } from 'react-toastify'
import { BsDatabase } from 'react-icons/bs'
import { Switch } from 'components/Switch'
import { BiChevronDown, BiChevronUp } from 'react-icons/bi'

const normalizarDescricaoEmpresaGrupo = (descricao?: string) =>
  (descricao || '').trim().toUpperCase()

const isGrupoEmpresaGrupo = (descricao?: string) => {
  const descricaoNormalizada = normalizarDescricaoEmpresaGrupo(descricao)

  return (
    descricaoNormalizada.startsWith('GRUPO -') ||
    descricaoNormalizada.startsWith('LOJA - GRUPO')
  )
}

const isLojaEmpresaGrupo = (descricao?: string) => {
  const descricaoNormalizada = normalizarDescricaoEmpresaGrupo(descricao)

  return (
    descricaoNormalizada.startsWith('LOJA -') && !isGrupoEmpresaGrupo(descricao)
  )
}

const tipoCampanhaElevenLabsId = '58dbb40f-ff45-4a29-8375-fcc9bb726d1f'

const Campanha = () => {
  const {
    register,
    optionsSelect,
    invalidsTabDadosGerais,
    visualizacao,
    tiposCampanha,
    activeTab
  } = useSelector<ApplicationState, CAMPANHA>((state) => state.campanha)

  const [selectKey, setSelectKey] = useState(() => Math.random())
  const [baseDadosIsOpen, setBaseDadosIsOpen] = useState(true)
  const [campanhaIsOpen, setCampanhaIsOpen] = useState(true)

  const { dispatch } = useRedux()
  const isCampanhaElevenLabs =
    register.tipoCampanhaCrmId === tipoCampanhaElevenLabsId
  const isEdicaoCampanhaElevenLabs = !!register.id && isCampanhaElevenLabs
  const isReadonly = visualizacao || isEdicaoCampanhaElevenLabs

  type Fields = keyof CAMPANHA['register']
  function onChange<T extends Fields>(
    value: T extends Fields ? CAMPANHA['register'][T] : never,
    field: T
  ) {
    if (isEdicaoCampanhaElevenLabs) {
      return
    }

    if (field === 'tipoCampanhaCrmId') {
      dispatch(campanhaActions.setRegisterFields([], 'empresasGrupo'))
      dispatch(
        campanhaActions.setRegisterFields(false, 'ignorarEnviadosElevenLabs')
      )
    }

    if (field === 'basePropria' || field === 'carteira') {
      dispatch(campanhaActions.setInvalidsTabDadosGerais(false, 'basePropria'))
      dispatch(campanhaActions.setInvalidsTabDadosGerais(false, 'carteira'))
    }

    dispatch(campanhaActions.setRegisterFields(value, field))
  }

  useEffect(() => {
    if (activeTab === 'register') {
      dispatch(campanhaActions.getTiposOperacaoSaga())
    }
  }, [dispatch, activeTab])

  useEffect(() => {
    const isCampanhaURA =
      register.tipoCampanhaCrmId === 'd38a3f12-927f-4936-8eee-6552ce2ed81e'

    if (!isCampanhaURA || !Array.isArray(register.empresasGrupo)) return

    const empresaComCodigo = register.empresasGrupo
      .map((sel) =>
        typeof sel === 'string'
          ? optionsSelect.empresasGrupos.find((x) => x.idGrupoELoja === sel)
          : sel
      )
      .find((item) => item?.codigoEmpresa)

    if (empresaComCodigo?.codigoEmpresa) {
      dispatch(campanhaActions.getPerguntasSaga(empresaComCodigo.codigoEmpresa))
    }
  }, [
    register.empresasGrupo,
    register.tipoCampanhaCrmId,
    optionsSelect.empresasGrupos,
    dispatch
  ])

  useEffect(() => {
    const isUraOuDiscador =
      register.tipoCampanhaCrmId === 'dfb8fbbe-d7b8-45d9-badf-7e1e18c943f9' || // DISCADOR
      register.tipoCampanhaCrmId === 'd38a3f12-927f-4936-8eee-6552ce2ed81e' // URA

    const empresasSelecionadas = register.empresasGrupo
      .map((sel) =>
        typeof sel === 'string'
          ? optionsSelect.empresasGrupos.find((x) => x.idGrupoELoja === sel)
          : sel
      )
      .filter((item) => !!item)

    const lojasSelecionadas = empresasSelecionadas.filter((item) =>
      isLojaEmpresaGrupo(item?.descricao)
    )
    const temGrupoSelecionado = empresasSelecionadas.some((item) =>
      isGrupoEmpresaGrupo(item?.descricao)
    )
    const [lojaSelecionada] = lojasSelecionadas

    const podeCarregarCategoria =
      isUraOuDiscador &&
      !temGrupoSelecionado &&
      lojasSelecionadas.length === 1 &&
      !!lojaSelecionada?.codigoEmpresa

    if (podeCarregarCategoria) {
      dispatch(
        campanhaActions.getCategoriasDiscadorSaga(lojaSelecionada.codigoEmpresa)
      )
    } else {
      dispatch(campanhaActions.setCategoriasDiscador([]))
      dispatch(
        campanhaActions.setNestedRegisterField(
          'campanhaCrmParametrosDiscador',
          'pabxipCategoriaSaida',
          null
        )
      )
    }
  }, [
    dispatch,
    optionsSelect.empresasGrupos,
    register.empresasGrupo,
    register.tipoCampanhaCrmId
  ])

  useEffect(() => {
    if (register.tipoCampanhaCrmId) {
      dispatch(campanhaActions.getGruposLojasSaga())
    }
  }, [dispatch, register.tipoCampanhaCrmId])

  return (
    <div className="d-flex flex-column row-gap-10">
      <Row>
        <div className="d-flex justify-content-between align-items-center w-100">
          <SimpleTitleCard>
            <BsDatabase className="icon" />
            Base De Dados
          </SimpleTitleCard>
          {baseDadosIsOpen ? (
            <BiChevronUp
              onClick={() => setBaseDadosIsOpen((prev) => !prev)}
              className="toggler"
            />
          ) : (
            <BiChevronDown
              onClick={() => setBaseDadosIsOpen((prev) => !prev)}
              className="toggler"
            />
          )}
        </div>

        <Collapse isOpen={baseDadosIsOpen}>
          <Row>
            <Col className="d-flex flex-column justify-content-end" md={3}>
              <label className="label-12">Utilizar base própria</label>
              <Switch
                onChange={(e) => {
                  const selecionado = e.target.checked
                  onChange(selecionado, 'basePropria')
                  if (selecionado) {
                    onChange(false, 'carteira')
                  }
                }}
                checked={register.basePropria}
                invalid={
                  invalidsTabDadosGerais.basePropria ||
                  invalidsTabDadosGerais.carteira
                }
                disabled={isReadonly}
              />
            </Col>
            <Col className="d-flex flex-column justify-content-end" md={3}>
              <label className="label-12">Utilizar consignado rápido</label>
              <Switch
                onChange={(e) => {
                  const selecionado = e.target.checked
                  onChange(selecionado, 'carteira')
                  if (selecionado) {
                    onChange(false, 'basePropria')
                  }
                }}
                checked={register.carteira}
                invalid={
                  invalidsTabDadosGerais.basePropria ||
                  invalidsTabDadosGerais.carteira
                }
                disabled={isReadonly}
              />
            </Col>
          </Row>
          <Row>
            <Col className="d-flex align-items-center gap-2" md={3}>
              <Input
                type="checkbox"
                onChange={(e) => {
                  onChange(e.target.checked, 'cliente')
                }}
                checked={register.basePropria ? register.cliente : false}
                disabled={visualizacao || !register.basePropria}
              />
              <label className="label-12 mt-1">Clientes</label>
            </Col>
          </Row>
        </Collapse>
      </Row>
      <hr />
      <div className="d-flex justify-content-between align-items-center">
        <SimpleTitleCard>
          <FiBriefcase className="icon" />
          Campanha
        </SimpleTitleCard>
        {campanhaIsOpen ? (
          <BiChevronUp
            onClick={() => setCampanhaIsOpen((prev) => !prev)}
            className="toggler"
          />
        ) : (
          <BiChevronDown
            onClick={() => setCampanhaIsOpen((prev) => !prev)}
            className="toggler"
          />
        )}
      </div>
      <Collapse isOpen={campanhaIsOpen}>
        <Row>
          <Col className="d-flex flex-column justify-content-end" md={6}>
            <label className="label-12">Tipos Campanha</label>
            <CustomSelect
              options={tiposCampanha}
              placeholder="Selecione"
              onChange={(e) => {
                onChange(e, 'tipoCampanhaCrmId')
              }}
              value={register.tipoCampanhaCrmId}
              disabled={isReadonly || !!register.id}
              accessorLabel="descricao"
              accessorValue="id"
            />
          </Col>
        </Row>
        <Row>
          <Col className="d-flex flex-column justify-content-end" md={6}>
            <label className="label-12">Descrição*</label>
            <InputCustom
              type="text"
              placeholder="Digite aqui"
              onChange={(e) => {
                onChange(e.target.value, 'descricao')
              }}
              value={register.descricao}
              invalid={invalidsTabDadosGerais.descricao}
              disabled={isReadonly}
            />
          </Col>
        </Row>
        <Row>
          <Col className="d-flex flex-column justify-content-start" md={6}>
            <label className="label-12">Tipo de operação</label>
            <CustomSelect
              options={optionsSelect.tiposOperacao}
              placeholder="Selecione"
              onChange={(e) => {
                onChange(e, 'tipoOperacaoId')
              }}
              value={register.tipoOperacaoId}
              accessorLabel="descricao"
              accessorValue="id"
              invalid={invalidsTabDadosGerais.tipoOperacaoId}
              disabled={isReadonly}
            />
          </Col>
          <Col className="d-flex flex-column justify-content-end">
            <CustomSelect
              key={selectKey}
              options={optionsSelect.empresasGrupos}
              isMulti
              placeholder="Selecione empresas/grupos"
              onChange={(e) => {
                const selecionadosIds = Array.isArray(e) ? e : e ? [e] : []

                const selecionadosCompletos =
                  optionsSelect.empresasGrupos.filter((opt) =>
                    selecionadosIds.some((sel) => sel === opt.idGrupoELoja)
                  )

                const lojasSelecionadas = selecionadosCompletos.filter((item) =>
                  isLojaEmpresaGrupo(item.descricao)
                )

                const temGrupoSelecionado = selecionadosCompletos.some((item) =>
                  isGrupoEmpresaGrupo(item.descricao)
                )

                const lojasSemCodigo = selecionadosCompletos.filter(
                  (item) =>
                    (!item.codigoEmpresa || item.codigoEmpresa.trim() === '') &&
                    isLojaEmpresaGrupo(item.descricao)
                )

                const isUraOuDiscador =
                  register.tipoCampanhaCrmId ===
                    'dfb8fbbe-d7b8-45d9-badf-7e1e18c943f9' ||
                  register.tipoCampanhaCrmId ===
                    'd38a3f12-927f-4936-8eee-6552ce2ed81e'

                const isElevenLabs =
                  register.tipoCampanhaCrmId === tipoCampanhaElevenLabsId

                if (isElevenLabs) {
                  const possuiMaisDeUmaLoja = lojasSelecionadas.length > 1

                  if (temGrupoSelecionado || possuiMaisDeUmaLoja) {
                    toast.warn(
                      'Para campanha ElevenLabs, selecione apenas uma loja, não é permitido criação em grupo.',
                      { autoClose: false }
                    )
                    setSelectKey(Math.random())
                    return
                  }
                }

                if (isUraOuDiscador) {
                  let erro = false
                  if (lojasSemCodigo.length > 0) {
                    erro = true
                    const nomes = lojasSemCodigo
                      .map((item) => item.descricao)
                      .join(', ')
                    toast.error(
                      `É necessário cadastrar o código de empresa para esta loja: ${nomes}`,
                      { autoClose: false }
                    )
                  }
                  selecionadosCompletos.forEach((selecionado) => {
                    if (selecionado.erros?.length > 0) {
                      erro = true
                      selecionado.erros.forEach((erro) => {
                        toast.warn(erro.erro, { autoClose: false })
                      })
                    }
                  })
                  if (erro) {
                    setSelectKey(Math.random())
                    return
                  }
                }

                onChange(selecionadosIds, 'empresasGrupo')
              }}
              value={register.empresasGrupo}
              accessorLabel="descricao"
              accessorValue="idGrupoELoja"
              invalid={invalidsTabDadosGerais.empresasGrupo}
              disabled={isReadonly || !!register.id}
            />
          </Col>
        </Row>
        <Row>
          <Col className="d-flex flex-column justify-content-end" md={3}>
            <label className="label-12">Data início campanha*</label>
            <InputCustom
              type="date"
              onInput={validarData}
              onChange={(e) => onChange(e.target.value, 'dataInicioCampanha')}
              value={register.dataInicioCampanha}
              invalid={invalidsTabDadosGerais.dataInicioCampanha}
              disabled={isReadonly}
            />
          </Col>
          <Col className="d-flex flex-column justify-content-end" md={3}>
            <label className="label-12">Data final campanha*</label>
            <InputCustom
              type="date"
              onInput={validarData}
              onChange={(e) => {
                onChange(e.target.value, 'dataFimCampanha')
              }}
              value={register.dataFimCampanha}
              invalid={invalidsTabDadosGerais.dataFimCampanha}
              disabled={isReadonly}
            />
          </Col>
        </Row>
        <Row>
          <Col className="d-flex flex-column justify-content-end" md={6}>
            <label className="label-12">Quantidade de clientes*</label>
            <InputCustom
              type="text"
              placeholder="Digite aqui"
              onChange={(e) => {
                onChange(
                  Number(maskNumber(e.target.value)),
                  'quantidadeClientes'
                )
              }}
              value={register.quantidadeClientes}
              invalid={invalidsTabDadosGerais.quantidadeClientes}
              disabled={isReadonly}
            />
          </Col>
        </Row>
      </Collapse>
    </div>
  )
}
export default Campanha
